import * as THREE from "three";
import type { BarrierConfig, LevelConfig, LevelLayer, LevelPiece, LevelPieceType } from "../level_config.js";
import type { LevelEntity } from "../types/level_entity.js";
import { TYPES } from "./block_showcase.js";
import { BlockMassTuner } from "./block_mass_tuner.js";
import { MODEL_POLYGONS } from "./model_polygons.js";
import { Renderer } from "../core/renderer.js";
import { SceneManager } from "../core/scene_manager.js";

const PIECE = {
  Cube: 1,
  Stone: 2,
  Jar: 3,
  Cone: 4,
  Metal: 5,
  Ice: 6,
  Tree: 7,
  Watermelon: 8,
  Plastic: 9,
  RoundMetal: 10,
  IronCan: 11,
} as const;

const COLUMNS = 9;
const ROWS = 5;
const LAYERS = 3;

const STATS_INTERVAL_MS = 500;
const PEAK_WINDOW_SAMPLES = 4;

const TABLES = [
  { difficulty: 0, name: "синій" },
  { difficulty: 1, name: "фіолетовий" },
  { difficulty: 2, name: "червоний" },
];

const BARRIER: BarrierConfig = { head: 0, mount: 1, x: 0, y: ROWS + 3 };

interface TextureSpec
{
  name: string;
  entity: string;
  type: number;
  variant?: number;
  width: number;
  height: number;
  cracked?: boolean;
  model: string | null;
}

interface GeometryStats
{
  meshes: number;
  triangles: number;
  vertices: number;
  polygons: number;
  approx: boolean;
}

function modelFor( type: number, variant: number | undefined, width: number, height: number ): string | null
{
  const length = Math.max( width, height );
  const square = width === 2 && height === 2;
  const capped = ( max: number ) => Math.min( length, max );

  switch ( type )
  {
    case PIECE.Cube:
      if ( variant === 1 )
        return null;
      return square ? "blocks/block_2x2.fbx" : `blocks/block_1x${capped( 3 )}.fbx`;
    case PIECE.Stone:
      return square ? "blocks/stone_2x2.fbx" : `blocks/stone_1x${capped( 4 )}.fbx`;
    case PIECE.Plastic:
      return square ? "blocks/plastic_block_2x2.fbx" : `blocks/plastic_block_1x${capped( 3 )}.fbx`;
    case PIECE.Jar:
      return `blocks/jam_1x${capped( 3 )}.fbx`;
    case PIECE.Metal:
      return `blocks/canned_block_cube_1x${capped( 3 )}.fbx`;
    case PIECE.RoundMetal:
      return `blocks/cylinder_colored_1x${capped( 3 )}.fbx`;
    case PIECE.IronCan:
      return `blocks/cylinder_grey_1x${capped( 3 )}.fbx`;
    case PIECE.Ice:
      return length === 1 ? "blocks/ice.fbx" : `blocks/block_1x${capped( 3 )}.fbx`;
    case PIECE.Tree:
      return `blocks/tree_block_1x${capped( 5 )}.fbx`;
    case PIECE.Cone:
      return variant !== undefined && variant >= 3 ? "blocks/cone.fbx" : "blocks/cone_crown.fbx";
    case PIECE.Watermelon:
      return "blocks/watermelon.fbx";
    default:
      return null;
  }
}

function spec(
  name: string,
  entity: string,
  type: number,
  variant: number | undefined,
  width = 1,
  height = 1,
  cracked = false
): TextureSpec
{
  return { name, entity, type, variant, width, height, cracked, model: modelFor( type, variant, width, height ) };
}

function buildSpecs(): TextureSpec[]
{
  const specs: TextureSpec[] = [];

  for ( const type of TYPES )
  {
    for ( let v = 0; v < type.variants; v++ )
    {
      if ( type.skip?.includes( v ) )
        continue;

      const variant = type.variants > 1 ? v : undefined;
      specs.push( spec( variant === undefined ? type.name : `${type.name} v${v}`, type.entity, type.type, variant ) );
    }
  }

  for ( const type of TYPES.filter( item => item.type === PIECE.RoundMetal || item.type === PIECE.Metal || item.type === PIECE.IronCan ) )
    for ( const length of [ 2, 3 ] )
      for ( let v = 0; v < type.variants; v++ )
        specs.push( spec( `${type.name} v${v} 1x${length}`, `${type.entity} 1x${length}`, type.type, v, 1, length ) );

  specs.push( spec( "Ice тріснутий", "IceBlock cracked", PIECE.Ice, undefined, 1, 1, true ) );

  for ( const v of [ 0, 2 ] )
    specs.push( spec( `Cube v${v} 2x2`, "PhysicsCube 2x2", PIECE.Cube, v, 2, 2 ) );

  for ( let v = 0; v < 8; v++ )
    specs.push( spec( `Plastic v${v} 2x2`, "PhysicsCube PLASTIC 2x2", PIECE.Plastic, v, 2, 2 ) );

  for ( let v = 0; v < 8; v++ )
    specs.push( spec( `Plastic v${v} 5x1`, "PhysicsCube PLASTIC атлас 1x5", PIECE.Plastic, v, 5, 1 ) );

  return specs;
}

const SPECS = buildSpecs();

function columnsFor( spec: TextureSpec ): number
{
  return Math.floor( COLUMNS / spec.width ) * spec.width;
}

function buildLayer( spec: TextureSpec ): LevelLayer
{
  const columns = columnsFor( spec );
  const grid: number[][] = Array.from( { length: ROWS }, () => Array( columns ).fill( 0 ) );
  const pieces: LevelPiece[] = [];
  let id = 1;

  for ( let y = ROWS - spec.height; y >= 0; y -= spec.height )
  {
    for ( let x = 0; x + spec.width <= columns; x += spec.width )
    {
      for ( let dy = 0; dy < spec.height; dy++ )
        for ( let dx = 0; dx < spec.width; dx++ )
          grid[y + dy][x + dx] = id;

      pieces.push( {
        id,
        x,
        y,
        width: spec.width,
        height: spec.height,
        type: spec.type as LevelPieceType,
        variant: spec.variant,
      } );
      id++;
    }
  }

  return { grid, pieces };
}

function blocksPerLevel( spec: TextureSpec ): number
{
  return buildLayer( spec ).pieces.length * LAYERS;
}

function geometryTriangles( geometry: THREE.BufferGeometry ): number
{
  const index = geometry.getIndex();
  return Math.floor( ( index ? index.count : geometry.getAttribute( "position" ).count ) / 3 );
}

const paired_polygons = new WeakMap<THREE.BufferGeometry, number>();

function pairedPolygons( geometry: THREE.BufferGeometry ): number
{
  const cached = paired_polygons.get( geometry );
  if ( cached !== undefined )
    return cached;

  const position = geometry.getAttribute( "position" );
  const index = geometry.getIndex();
  const count = geometryTriangles( geometry );
  const vertexAt = ( triangle: number, corner: number ) => index ? index.getX( triangle * 3 + corner ) : triangle * 3 + corner;
  const keyOf = ( vertex: number ) =>
    `${Math.round( position.getX( vertex ) * 1e4 )},${Math.round( position.getY( vertex ) * 1e4 )},${Math.round( position.getZ( vertex ) * 1e4 )}`;

  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const first = new THREE.Vector3();
  const second = new THREE.Vector3();
  const normalOf = ( triangle: number, out: THREE.Vector3 ) =>
  {
    a.fromBufferAttribute( position, vertexAt( triangle, 0 ) );
    b.fromBufferAttribute( position, vertexAt( triangle, 1 ) );
    c.fromBufferAttribute( position, vertexAt( triangle, 2 ) );
    return out.subVectors( c, b ).cross( a.sub( b ) ).normalize();
  };

  let polygons = 0;
  for ( let t = 0; t < count; t++ )
  {
    polygons++;
    if ( t + 1 >= count )
      break;

    const keys = new Set( [ 0, 1, 2 ].map( corner => keyOf( vertexAt( t, corner ) ) ) );
    let shared = 0;
    for ( let corner = 0; corner < 3; corner++ )
      if ( keys.has( keyOf( vertexAt( t + 1, corner ) ) ) )
        shared++;

    if ( shared === 2 && normalOf( t, first ).dot( normalOf( t + 1, second ) ) > 0.9 )
      t++;
  }

  paired_polygons.set( geometry, polygons );
  return polygons;
}

function emptyStats(): GeometryStats
{
  return { meshes: 0, triangles: 0, vertices: 0, polygons: 0, approx: false };
}

function addStats( total: GeometryStats, part: GeometryStats ): void
{
  total.meshes += part.meshes;
  total.triangles += part.triangles;
  total.vertices += part.vertices;
  total.polygons += part.polygons;
  total.approx ||= part.approx;
}

function blockStats( root: THREE.Object3D, model: string | null ): GeometryStats
{
  const stats = emptyStats();
  let paired = 0;

  root.traverse( child =>
  {
    if ( !( child instanceof THREE.Mesh ) || !child.visible )
      return;

    const geometry = child.geometry as THREE.BufferGeometry;
    stats.meshes++;
    stats.triangles += geometryTriangles( geometry );
    stats.vertices += geometry.getAttribute( "position" ).count;

    if ( !model )
      paired += pairedPolygons( geometry );
  } );

  const source = model ? MODEL_POLYGONS[model] : undefined;
  if ( source )
    stats.polygons = Math.round( stats.triangles * source.polygons / source.triangles );
  else
  {
    stats.polygons = paired;
    stats.approx = true;
  }

  return stats;
}

function sceneStats( root: THREE.Object3D ): GeometryStats
{
  const stats = emptyStats();
  stats.approx = true;

  root.traverseVisible( child =>
  {
    if ( !( child instanceof THREE.Mesh ) )
      return;

    const geometry = child.geometry as THREE.BufferGeometry;
    const instances = child instanceof THREE.InstancedMesh ? child.count : 1;
    if ( instances === 0 )
      return;

    const triangles = geometryTriangles( geometry ) * instances;
    stats.meshes++;
    stats.triangles += triangles;
    stats.vertices += geometry.getAttribute( "position" ).count * instances;
    stats.polygons += pairedPolygons( geometry ) * instances;
  } );

  return stats;
}

function formatNumber( value: number ): string
{
  return Math.round( value ).toString().replace( /\B(?=(\d{3})+(?!\d))/g, " " );
}

function describe( stats: GeometryStats ): string
{
  const approx = stats.approx ? "≈" : "";
  return `${formatNumber( stats.triangles )} трикутн · ${approx}${formatNumber( stats.polygons )} полігон · ` +
    `${formatNumber( stats.vertices )} верш · ${formatNumber( stats.meshes )} меш`;
}

export class TextureTest
{
  public static readonly BASE_LEVEL = 90200;
  public static readonly COUNT = SPECS.length;

  public static show_level: ( ( level: number ) => void ) | null = null;

  private static active = false;
  private static index = 0;
  private static table_index = 2;
  private static with_barrier = false;
  private static controls_open = false;

  private static entity_source: ( () => LevelEntity[] ) | null = null;
  private static block_stats: GeometryStats | null = null;
  private static stats_timer: number | null = null;
  private static frame_samples: { triangles: number; calls: number }[] = [];

  private static root: HTMLDivElement | null = null;
  private static caption: HTMLDivElement | null = null;
  private static stats_el: HTMLDivElement | null = null;
  private static controls: HTMLDivElement | null = null;
  private static table_btn: HTMLButtonElement | null = null;
  private static barrier_btn: HTMLButtonElement | null = null;
  private static mass_slider: HTMLInputElement | null = null;
  private static mass_field: HTMLInputElement | null = null;
  private static mass_note: HTMLSpanElement | null = null;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static isTextureLevel( level: number ): boolean
  {
    return level >= this.BASE_LEVEL && level < this.BASE_LEVEL + this.COUNT;
  }

  public static getIndex(): number
  {
    return this.index;
  }

  public static getName( index: number = this.index ): string
  {
    return SPECS[( index + this.COUNT ) % this.COUNT].name;
  }

  public static getEntity( index: number = this.index ): string
  {
    return SPECS[( index + this.COUNT ) % this.COUNT].entity;
  }

  public static names(): string[]
  {
    return SPECS.map( s => s.name );
  }

  public static currentLevel(): number
  {
    return this.BASE_LEVEL + this.index;
  }

  public static activate( index: number ): number
  {
    this.active = true;
    this.index = ( ( index % this.COUNT ) + this.COUNT ) % this.COUNT;
    return this.currentLevel();
  }

  public static advance(): number
  {
    return this.activate( this.index + 1 );
  }

  public static back(): number
  {
    return this.activate( this.index - 1 );
  }

  public static deactivate(): void
  {
    this.active = false;
    BlockMassTuner.setActiveKey( null );
    this.removeOverlay();
  }

  public static hideBar(): void
  {
    this.removeOverlay();
  }

  public static beginLevel( level: number ): void
  {
    this.active = true;
    this.index = level - this.BASE_LEVEL;
    this.entity_source = null;
    this.block_stats = null;
    this.frame_samples = [];
    BlockMassTuner.setActiveKey( this.getName() );
    this.buildOverlay();
  }

  public static onLevelBuilt( entities: () => LevelEntity[] ): void
  {
    const current = SPECS[this.index];
    this.entity_source = entities;

    const list = entities();
    if ( current.cracked )
      for ( const entity of list )
        ( entity as LevelEntity & { crack?: ( point?: THREE.Vector3, silent?: boolean ) => void } ).crack?.( undefined, true );

    this.block_stats = list.length > 0 ? blockStats( list[0].mesh, current.model ) : null;
    this.refreshStats();
  }

  public static stats(): { block: GeometryStats | null; level: GeometryStats; scene: GeometryStats; frame: { triangles: number; calls: number } }
  {
    return {
      block: this.block_stats,
      level: this.levelStats(),
      scene: sceneStats( SceneManager.scene ),
      frame: this.frameInfo(),
    };
  }

  public static resetMasses(): void
  {
    BlockMassTuner.resetAll();
    this.reloadLevel();
  }

  private static reloadLevel(): void
  {
    this.syncMassControls();
    this.show_level?.( this.currentLevel() );
  }

  public static setBlockMass( mass: number ): void
  {
    this.setMass( mass );
    this.reloadLevel();
  }

  private static setMass( mass: number ): void
  {
    BlockMassTuner.setMass( mass );
    this.syncMassControls();
  }

  private static syncMassControls(): void
  {
    const base = BlockMassTuner.getBaseMass();
    const mass = BlockMassTuner.getMass();

    if ( this.mass_slider )
    {
      this.mass_slider.disabled = mass === null;
      this.mass_slider.value = String(
        mass === null
          ? 0
          : Math.min( BlockMassTuner.MASS_MAX, Math.max( BlockMassTuner.MASS_MIN, mass ) )
      );
    }

    if ( this.mass_field && this.mass_field !== document.activeElement )
    {
      this.mass_field.disabled = mass === null;
      this.mass_field.value = mass === null ? "" : String( Number( mass.toFixed( 2 ) ) );
    }

    if ( this.mass_note )
      this.mass_note.textContent = base === null
        ? "база ?"
        : `база ${Number( base.toFixed( 2 ) )} · ×${BlockMassTuner.getMultiplier().toFixed( 2 )}`;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    if ( !this.isTextureLevel( level ) )
      return null;

    const current = SPECS[level - this.BASE_LEVEL];

    return {
      level,
      difficulty: TABLES[this.table_index].difficulty as LevelConfig["difficulty"],
      layers: Array.from( { length: LAYERS }, () => buildLayer( current ) ),
      barriers: this.with_barrier ? [ { ...BARRIER } ] : undefined,
    };
  }

  private static go( delta: number ): void
  {
    const level = this.activate( this.index + delta );
    BlockMassTuner.setActiveKey( this.getName() );
    this.updateCaption();
    this.syncMassControls();
    this.show_level?.( level );
  }

  private static levelStats(): GeometryStats & { blocks: number }
  {
    const total = { ...emptyStats(), blocks: 0 };
    const model = SPECS[this.index].model;

    for ( const entity of this.entity_source?.() ?? [] )
    {
      if ( !entity.isAlive() )
        continue;

      total.blocks++;
      addStats( total, blockStats( entity.mesh, model ) );
    }

    return total;
  }

  private static frameInfo(): { triangles: number; calls: number }
  {
    const info = Renderer.renderer?.info.render;
    return { triangles: info?.triangles ?? 0, calls: info?.calls ?? 0 };
  }

  private static refreshStats(): void
  {
    if ( !this.stats_el )
      return;

    const frame = this.frameInfo();
    this.frame_samples.push( frame );
    if ( this.frame_samples.length > PEAK_WINDOW_SAMPLES )
      this.frame_samples.shift();

    const peak_triangles = Math.max( ...this.frame_samples.map( s => s.triangles ) );
    const peak_calls = Math.max( ...this.frame_samples.map( s => s.calls ) );
    const level = this.levelStats();
    const scene = sceneStats( SceneManager.scene );
    const memory = Renderer.renderer?.info.memory;
    const programs = Renderer.renderer?.info.programs?.length ?? 0;

    const lines: [ string, string ][] = [
      [ "1 блок", this.block_stats ? describe( this.block_stats ) : "—" ],
      [ `Рівень ×${level.blocks}`, describe( level ) ],
      [ "Уся сцена", describe( scene ) ],
      [ "Кадр GPU", `${formatNumber( frame.triangles )} трикутн · ${frame.calls} draw · пік 2с ${formatNumber( peak_triangles )} / ${peak_calls}` ],
      [ "GPU памʼять", `${memory?.geometries ?? 0} геометрій · ${memory?.textures ?? 0} текстур · ${programs} шейдерів` ],
    ];

    this.stats_el.innerHTML = "";
    for ( const [ label, value ] of lines )
    {
      const row = document.createElement( "div" );
      row.style.display = "flex";
      row.style.gap = "6px";

      const head = document.createElement( "span" );
      head.textContent = label;
      head.style.flex = "0 0 74px";
      head.style.opacity = "0.6";

      const body = document.createElement( "span" );
      body.textContent = value;
      body.style.flex = "1";
      body.style.minWidth = "0";

      row.appendChild( head );
      row.appendChild( body );
      this.stats_el.appendChild( row );
    }
  }

  private static updateCaption(): void
  {
    if ( !this.caption )
      return;

    const current = SPECS[this.index];
    this.caption.innerHTML = "";

    const title = document.createElement( "div" );
    title.style.fontSize = "14px";
    title.style.fontWeight = "700";
    title.style.whiteSpace = "nowrap";
    title.style.overflow = "hidden";
    title.style.textOverflow = "ellipsis";
    title.textContent = `${this.index + 1}/${this.COUNT}  ${current.name}`;

    const sub = document.createElement( "div" );
    sub.style.fontSize = "10px";
    sub.style.opacity = "0.7";
    sub.style.whiteSpace = "nowrap";
    sub.style.overflow = "hidden";
    sub.style.textOverflow = "ellipsis";
    sub.textContent = `${current.entity} · ${blocksPerLevel( current )} шт · ${current.model ?? "процедурна / glb"}`;

    this.caption.appendChild( title );
    this.caption.appendChild( sub );
  }

  private static updateToggles(): void
  {
    if ( this.table_btn )
      this.table_btn.textContent = `Стіл: ${TABLES[this.table_index].name}`;

    if ( this.barrier_btn )
    {
      this.barrier_btn.textContent = `Барʼєр: ${this.with_barrier ? "так" : "ні"}`;
      this.barrier_btn.style.background = this.with_barrier ? "#2e7d32" : "#37474f";
    }

    if ( this.controls )
      this.controls.style.display = this.controls_open ? "flex" : "none";
  }

  private static removeOverlay(): void
  {
    BlockMassTuner.setBaseListener( null );

    if ( this.stats_timer !== null )
      window.clearInterval( this.stats_timer );

    this.stats_timer = null;
    this.root?.remove();
    this.root = null;
    this.caption = null;
    this.stats_el = null;
    this.controls = null;
    this.table_btn = null;
    this.barrier_btn = null;
    this.mass_slider = null;
    this.mass_field = null;
    this.mass_note = null;
  }

  private static buildOverlay(): void
  {
    this.removeOverlay();

    const root = document.createElement( "div" );
    root.id = "textureTestBar";
    root.style.position = "fixed";
    root.style.left = "0";
    root.style.right = "0";
    root.style.bottom = "calc(56px + env(safe-area-inset-bottom, 0px))";
    root.style.zIndex = "2147483000";
    root.style.display = "flex";
    root.style.flexDirection = "column";
    root.style.gap = "6px";
    root.style.padding = "6px 6px 6px 88px";
    root.style.boxSizing = "border-box";
    root.style.background = "rgba(10, 12, 20, 0.82)";
    root.style.font = "12px Arial, sans-serif";
    root.style.color = "#fff";
    root.style.touchAction = "none";

    const stop = ( event: Event ) => event.stopPropagation();
    for ( const name of [ "pointerdown", "pointermove", "pointerup", "click", "touchstart", "touchmove", "touchend" ] )
      root.addEventListener( name, stop );

    const button = ( text: string, action: () => void, background = "#1e88e5" ): HTMLButtonElement =>
    {
      const el = document.createElement( "button" );
      el.type = "button";
      el.textContent = text;
      el.style.minWidth = "44px";
      el.style.minHeight = "44px";
      el.style.padding = "0 8px";
      el.style.fontSize = "18px";
      el.style.fontWeight = "700";
      el.style.color = "#fff";
      el.style.background = background;
      el.style.border = "none";
      el.style.borderRadius = "8px";
      el.addEventListener( "click", action );
      return el;
    };

    const row = (): HTMLDivElement =>
    {
      const el = document.createElement( "div" );
      el.style.display = "flex";
      el.style.alignItems = "stretch";
      el.style.gap = "6px";
      el.style.minWidth = "0";
      return el;
    };

    const stats_el = document.createElement( "div" );
    stats_el.style.font = "10.5px/1.35 Menlo, Consolas, monospace";
    stats_el.style.display = "flex";
    stats_el.style.flexDirection = "column";
    stats_el.style.gap = "1px";

    const caption = document.createElement( "div" );
    caption.style.flex = "1";
    caption.style.minWidth = "0";
    caption.style.display = "flex";
    caption.style.flexDirection = "column";
    caption.style.justifyContent = "center";
    caption.style.lineHeight = "1.25";

    const nav = row();
    nav.appendChild( button( "‹", () => this.go( -1 ) ) );
    nav.appendChild( caption );
    nav.appendChild( button( "›", () => this.go( 1 ) ) );
    nav.appendChild( button( "⚙", () =>
    {
      this.controls_open = !this.controls_open;
      this.updateToggles();
    }, "#37474f" ) );

    const controls = document.createElement( "div" );
    controls.style.display = "none";
    controls.style.flexDirection = "column";
    controls.style.gap = "6px";

    const picker = document.createElement( "select" );
    picker.style.flex = "1";
    picker.style.minWidth = "0";
    picker.style.minHeight = "44px";
    picker.style.fontSize = "14px";
    picker.style.color = "#fff";
    picker.style.background = "#263238";
    picker.style.border = "none";
    picker.style.borderRadius = "8px";

    SPECS.forEach( ( item, i ) =>
    {
      const option = document.createElement( "option" );
      option.value = String( i );
      option.textContent = `${i + 1}. ${item.name}`;
      picker.appendChild( option );
    } );

    picker.value = String( this.index );
    picker.addEventListener( "change", () =>
    {
      this.go( Number( picker.value ) - this.index );
    } );

    const reset_btn = button( "⟳", () => this.resetMasses(), "#c62828" );
    reset_btn.title = "Скинути маси всіх блоків";

    const pick_row = row();
    pick_row.appendChild( picker );
    pick_row.appendChild( reset_btn );

    const toggle_row = row();
    const table_btn = button( "", () =>
    {
      this.table_index = ( this.table_index + 1 ) % TABLES.length;
      this.updateToggles();
      this.reloadLevel();
    }, "#37474f" );
    table_btn.style.flex = "1";
    table_btn.style.fontSize = "13px";

    const barrier_btn = button( "", () =>
    {
      this.with_barrier = !this.with_barrier;
      this.updateToggles();
      this.reloadLevel();
    } );
    barrier_btn.style.flex = "1";
    barrier_btn.style.fontSize = "13px";

    toggle_row.appendChild( table_btn );
    toggle_row.appendChild( barrier_btn );

    const mass_box = row();
    mass_box.style.alignItems = "center";

    const mass_title = document.createElement( "span" );
    mass_title.textContent = "МАСА";
    mass_title.style.fontSize = "11px";
    mass_title.style.opacity = "0.7";

    const mass_slider = document.createElement( "input" );
    mass_slider.type = "range";
    mass_slider.min = String( BlockMassTuner.MASS_MIN );
    mass_slider.max = String( BlockMassTuner.MASS_MAX );
    mass_slider.step = "0.5";
    mass_slider.style.flex = "1";
    mass_slider.style.minWidth = "0";

    const mass_field = document.createElement( "input" );
    mass_field.type = "number";
    mass_field.step = "0.5";
    mass_field.style.width = "56px";
    mass_field.style.padding = "6px 4px";
    mass_field.style.borderRadius = "4px";
    mass_field.style.border = "1px solid rgba(255,255,255,0.25)";
    mass_field.style.background = "rgba(255,255,255,0.08)";
    mass_field.style.color = "#fff";
    mass_field.style.font = "700 13px Arial, sans-serif";
    mass_field.style.textAlign = "right";

    const mass_note = document.createElement( "span" );
    mass_note.style.fontSize = "10px";
    mass_note.style.opacity = "0.55";
    mass_note.style.whiteSpace = "nowrap";

    mass_box.appendChild( mass_title );
    mass_box.appendChild( mass_slider );
    mass_box.appendChild( mass_field );
    mass_box.appendChild( mass_note );

    mass_slider.addEventListener( "input", () => this.setMass( Number( mass_slider.value ) ) );
    mass_slider.addEventListener( "change", () => this.reloadLevel() );

    const commitField = (): void =>
    {
      const typed = Number( mass_field.value );
      if ( Number.isFinite( typed ) )
        this.setMass( typed );
      this.reloadLevel();
    };

    mass_field.addEventListener( "change", commitField );
    mass_field.addEventListener( "keydown", event =>
    {
      if ( ( event as KeyboardEvent ).key === "Enter" )
        commitField();
    } );

    controls.appendChild( pick_row );
    controls.appendChild( toggle_row );
    controls.appendChild( mass_box );

    root.appendChild( stats_el );
    root.appendChild( nav );
    root.appendChild( controls );

    document.body.appendChild( root );

    this.root = root;
    this.caption = caption;
    this.stats_el = stats_el;
    this.controls = controls;
    this.table_btn = table_btn;
    this.barrier_btn = barrier_btn;
    this.mass_slider = mass_slider;
    this.mass_field = mass_field;
    this.mass_note = mass_note;
    BlockMassTuner.setBaseListener( () => this.syncMassControls() );
    this.updateCaption();
    this.updateToggles();
    this.syncMassControls();
    this.refreshStats();
    this.stats_timer = window.setInterval( () => this.refreshStats(), STATS_INTERVAL_MS );
  }
}
